import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils'
import { Dropdown, OverlayTrigger, Pagination, Table, Tooltip } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import threeDots from '../../../../../../_metronic/assets/images/svg/investor/threeDots.svg'
import { useAuth } from '../../../../auth'
import { useParams } from 'react-router-dom'
import { deleteShareTransaction, getShareTransactions } from '../../core/_requests'
import SkeletonRow from '../../../../digital-signing/components/SkeletonRow'
import NoDataPage from '../../../../widgets/components/NoDataPage'
import { toast } from 'react-toastify'
import { DeleteFounderPromoterModal } from '../../../founders-and-promoters/components/DeleteFounderPromoterModal'
import {
  formatCurrency,
  formatNumber,
  formatTransactionId,
  getCurrencySymbol,
  parseCurrency,
} from '../../../../../core/_util'
import { TransactionModalType } from '../../core/_models'
import ShareTransactionDetailsModal from './share-transaction-details-modal'
import copyicon from '../../../../../../_metronic/assets/images/svg/copy-svg.svg'
import AddSharesModal from './add-shares-modal'
import { useDateFormat } from '../../../../../hooks/useDateFormat'
import { useCurrency } from '../../../../../hooks/useCurrency'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../../../hooks/gtmHelpers'
import { useThemeMode } from '../../../../../../_metronic/partials'

declare module '@tanstack/react-table' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>
  }
  interface FilterMeta {
    itemRank: RankingInfo
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)
  addMeta({ itemRank })
  return itemRank.passed
}

interface ShareTransaction {
  shareTransactionId: string
  issueDate: string
  certificateCode: string
  shareClass: {
    shareTransactionId: string
    className: string
    tagColor: string
  }
  numberOfShares: number
  sharesValue: number
}

export const DataTableServerShareTransaction = ({
  openModal,
  closeModal,
  activeModal,
  setIsCreate,
  selectedShareTransactionSortFilter,
  selectedShareTransactionDateFilter,
  globalFilter,
  setGlobalFilter,
  isShareTransactionCreateOrUpdate,
  setIsShareTransactionCreateOrUpdate,
  setSidebar,
  setModalType,
  setIsShareClassDropdown,
}: {
  openModal: (modalType: TransactionModalType) => void
  closeModal: () => void
  activeModal: string
  setIsCreate: Dispatch<SetStateAction<boolean>>
  selectedShareTransactionSortFilter: string
  selectedShareTransactionDateFilter: string
  globalFilter: any
  setGlobalFilter: Dispatch<SetStateAction<any>>
  isShareTransactionCreateOrUpdate: boolean
  setIsShareTransactionCreateOrUpdate: Dispatch<SetStateAction<boolean>>
  setSidebar: Dispatch<SetStateAction<number>>
  setModalType: Dispatch<SetStateAction<TransactionModalType>>
  setIsShareClassDropdown: Dispatch<SetStateAction<boolean>>
}) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const { formatMessage } = useIntl()
  const { mode } = useThemeMode()
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>()
  const { shareholderId } = useParams()
  const { getDateValue } = useDateFormat()
  const [modalContainer, setModalContainer] = useState<HTMLElement | null>(null)
  const [pagination, setPagination] = useState<{
    currentPage: number
    pageSize: number
    totalCount: number
    nextPage: number
    prevPage: number | null
    totalPages: number
  }>({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    nextPage: 0,
    prevPage: null,
    totalPages: 0,
  })
  const { companyId, companyCurrency, currentUser } = useAuth()
  const [data, setData] = useState<ShareTransaction[]>([])
  const [loading, setLoading] = useState(false)
  const [shareTransactionId, setShareTransactionId] = useState<string | null>(null)
  const [showDeleteModal, setDeleteShowModal] = useState(false)
  const { currencyOptions } = useCurrency()
  const [deleteLoading, setDeleteLoading] = useState(false)

  const multiFieldGlobalFilter: FilterFn<ShareTransaction> = (row, columnId, value, addMeta) => {
    // Check if the search term is in the email or the combined name fields
    const classNameMatch = rankItem(`${row.original.shareClass.className}`, value)

    // Determine which match to use based on rank comparison or other logic
    let bestMatch: RankingInfo | undefined
    if (classNameMatch.passed) {
      bestMatch = classNameMatch
    }

    // Only call addMeta if a best match is found
    if (bestMatch) {
      addMeta({ itemRank: bestMatch })
    }

    // Return whether the search term passed in either field
    return !!bestMatch
  }

  const columns = React.useMemo<ColumnDef<ShareTransaction, any>[]>(
    () => [
      {
        accessorKey: 'issueDate',
        id: 'issueDate',
        header: 'Issue Date',
        cell: (info) => {
          const row = info.row.original
          return (
            <div
              className='cursor-pointer'
              onClick={() => {
                setIsCreate(false)
                setShareTransactionId(row.shareTransactionId)
                openModal('ShareTransactionDetails')
              }}
            >
              <span className='ms-2'>{getDateValue(row.issueDate)}</span>
            </div>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'shareTransactionId',
        id: 'Transaction ID',
        header: 'Transaction ID',
        cell: (info) => {
          const row = info.row.original
          const transactionId = row?.shareTransactionId ?? '-'
          const formattedTransactionId = formatTransactionId(row?.shareTransactionId ?? '-')

          const handleCopy = () => {
            navigator.clipboard.writeText(transactionId)
            toast.success('Transaction ID copied to clipboard!')
          }

          return (
            <div
              style={{ display: 'flex', alignItems: 'center' }}
              onClick={() => {
                setIsCreate(false)
                setShareTransactionId(row.shareTransactionId)
                openModal('ShareTransactionDetails')
              }}
            >
              <span>{formattedTransactionId}</span>
              {transactionId !== '-' && (
                <img
                  src={copyicon}
                  alt='copy-icon'
                  onClick={handleCopy}
                  style={{ cursor: 'pointer', marginLeft: '8px', width: '16px', height: '16px' }} // Adjust size as needed
                  title='Copy Transaction ID'
                />
              )}
            </div>
          )
        },
        filterFn: 'includesString',
        enableSorting: true,
      },
      {
        accessorKey: 'certificateCode',
        id: 'certificateCode',
        header: 'Certificate Code',
        cell: (info) => {
          const row = info.row.original
          return (
            <div
              className='cursor-pointer'
              onClick={() => {
                setIsCreate(false)
                setShareTransactionId(row.shareTransactionId)
                openModal('ShareTransactionDetails')
              }}
            >
              <span className='ms-2'>{row.certificateCode ?? '-'}</span>
            </div>
          )
        },
        filterFn: 'includesString',
      },

      {
        accessorKey: 'shareClass',
        id: 'shareClass',
        header: 'Share Class',
        cell: (info) => {
          const row = info.row.original
          return (
            <div
              className='cursor-pointer'
              onClick={() => {
                setIsCreate(false)
                setShareTransactionId(row.shareTransactionId)
                openModal('ShareTransactionDetails')
              }}
            >
              <span className='' style={{ color: `${row.shareClass.tagColor}` }}>
                {row.shareClass.className}
              </span>
            </div>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'numberOfShares',
        id: 'numberOfShares',
        header: 'No. of Shares',
        cell: (info) => {
          const row = info.row.original
          return (
            <div
              className='cursor-pointer'
              onClick={() => {
                setIsCreate(false)
                setShareTransactionId(row.shareTransactionId)
                openModal('ShareTransactionDetails')
              }}
            >
              <div>
                <span className='ms-2'>
                  {row.numberOfShares ? formatNumber(row.numberOfShares) : '-'}
                </span>
              </div>
            </div>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'sharesValue',
        id: 'sharesValue',
        header: 'Shares Value',
        cell: (info) => {
          const row = info.row.original
          return (
            <div
              className='cursor-pointer'
              onClick={() => {
                setIsCreate(false)
                setShareTransactionId(row.shareTransactionId)
                openModal('ShareTransactionDetails')
              }}
            >
              <div>
                <span ref={(node) => setModalContainer(node)}>
                  <OverlayTrigger
                    placement='bottom'
                    overlay={
                      <Tooltip id='tooltip'>
                        <p className='m-0'>{`${companyCurrency} 
                      ${formatCurrency(row?.sharesValue, companyCurrency)}`}</p>
                      </Tooltip>
                    }
                    container={modalContainer}
                  >
                    <p className='font-size-13 mb-0'>
                      {row?.sharesValue ? (
                        <>
                          {`${companyCurrency} ${getCurrencySymbol(
                            companyCurrency,
                            currencyOptions
                          )}${parseCurrency(row?.sharesValue)}`}
                        </>
                      ) : (
                        '-'
                      )}
                    </p>
                  </OverlayTrigger>
                </span>
              </div>
            </div>
          )
        },
        filterFn: 'includesString',
      },
    ],
    [companyCurrency] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const fetchShareTransactions = async () => {
    if (!companyId) return
    if (!shareholderId) return
    setLoading(true)
    try {
      const response = await getShareTransactions(
        shareholderId,
        companyId,
        pagination.currentPage,
        pagination.pageSize,
        selectedShareTransactionSortFilter,
        selectedShareTransactionDateFilter
      )
      if (response.data.success) {
        setPagination((prev) => ({
          ...prev,
          currentPage: response.data.data.pagination.currentPage,
          pageSize: response.data.data.pagination.pageSize,
          totalCount: response.data.data.pagination.totalRecords,
          nextPage: response.data.data.pagination.nextPage,
          prevPage: response.data.data.pagination.prevPage,
          totalPages: response.data.data.pagination.totalPages,
        }))

        setData(response.data.data.shareTransactions)
        setIsShareTransactionCreateOrUpdate(false)
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.ShareTransactionAccessSuccessful,
            action: 'normal',
            metadata: {
              company_id: companyId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              share_transaction_details: response.data.data.shareTransactions,
            },
          },
        })
      } else {
        toast.error(response.data.errors[0])
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.ShareTransactionAccessFailed,
            action: 'normal',
            metadata: {
              company_id: companyId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              errors: response.data.errors,
            },
          },
        })
      }
    } catch (error) {
      console.error('Error fetching founders/promoters:', error)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.ShareTransactionAccessFailed,
          action: 'normal',
          metadata: {
            company_id: companyId,
            user_id: currentUser?.userId,
            email: currentUser?.email,
            error: error,
          },
        },
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchShareTransactions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyId,
    pagination.currentPage,
    pagination.pageSize,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    isShareTransactionCreateOrUpdate === true,
    selectedShareTransactionSortFilter,
    selectedShareTransactionDateFilter,
  ]) // eslint-disable-line react-hooks/exhaustive-deps

  const table = useReactTable({
    data,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    manualPagination: true,
    rowCount: pagination.totalCount,
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: multiFieldGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  const handlePageChange = (pageNumber: number) => {
    setPagination((prev) => ({ ...prev, currentPage: pageNumber }))
  }

  const handleDeleteShareTransaction = async () => {
    if (!shareTransactionId) return
    if (!companyId) return
    if (!shareholderId) return
    setDeleteLoading(true)
    try {
      const {
        data: { success, errors },
      } = await deleteShareTransaction(companyId, shareTransactionId, shareholderId)
      if (success) {
        toast.success(formatMessage({ id: 'Share transaction deleted successfully' }))
        await fetchShareTransactions()
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.DeleteShareTransactionSuccessful,
            action: 'normal',
            metadata: {
              company_id: companyId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              share_transaction_id: shareTransactionId,
            },
          },
        })
      } else {
        toast.error(errors[0])
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.DeleteShareTransactionFailed,
            action: 'normal',
            metadata: {
              company_id: companyId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              share_transaction_id: shareTransactionId,
              errors: errors,
            },
          },
        })
      }
    } catch (error) {
      console.log(error)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.DeleteShareTransactionFailed,
          action: 'normal',
          metadata: {
            company_id: companyId,
            user_id: currentUser?.userId,
            email: currentUser?.email,
            share_transaction_id: shareTransactionId,
            error: error,
          },
        },
      })
    } finally {
      setDeleteLoading(false)
      setDeleteShowModal(false)
    }
  }

  return (
    <>
      <div style={{ overflowX: 'auto' }}>
        <div className='bg-white rounded-2 p-4'>
          {!loading && (data?.length === 0 || table.getRowModel().rows.length === 0) ? (
            <div className='my-5 mx-6'>
              <NoDataPage width={'w-0'} />
            </div>
          ) : (
            <Table className='w-100' style={{ minWidth: '600px' }}>
              <thead className='custom-thead border-bottom'>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className='font-size-12 font-weight-400 table-row border-dark-mode'
                  >
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        className='min-w-120px pb-6 pt-4'
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            className={
                              header.column.getCanSort() ? 'cursor-pointer select-none' : ''
                            }
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {{
                              asc: ' ▲',
                              desc: ' ▼',
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        )}
                      </th>
                    ))}
                    <th className='min-w-120px pb-6 pt-4'>{formatMessage({ id: 'Actions' })}</th>
                  </tr>
                ))}
              </thead>
              <tbody>
                {loading || currentUser?.company[0]?.currency?.code !== companyCurrency ? (
                  <>
                    <SkeletonRow count={8} />
                  </>
                ) : (
                  <>
                    {table.getRowModel().rows.map((row, index) => (
                      <tr key={row.id} className='border-bottom data-table-link-color'>
                        {row.getVisibleCells().map((cell, index) => {
                          return (
                            <>
                              {index === 3 ? (
                                <td key={cell.id} className='pt-5 border-0'>
                                  <div
                                    style={{
                                      backgroundColor: `rgba(${parseInt(
                                        cell.row.original.shareClass.tagColor.slice(1, 3),
                                        16
                                      )}, 
                            ${parseInt(cell.row.original.shareClass.tagColor.slice(3, 5), 16)}, 
                            ${parseInt(cell.row.original.shareClass.tagColor.slice(5, 7), 16)}, 
                            0.4)`,
                                      opacity: '0.8',
                                      padding: '5px',
                                      width: '70%',
                                      borderRadius: '5px',
                                    }}
                                  >
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                  </div>
                                </td>
                              ) : (
                                <td key={cell.id} className='pt-5 border-0 cursor-pointer'>
                                  <div>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                  </div>
                                </td>
                              )}
                            </>
                          )
                        })}
                        <td className='pt-5' onClick={(e) => e.stopPropagation()}>
                          <div className='dropdown-container d-flex justify-content-center align-items-center pb-1'>
                            <Dropdown
                              show={index === selectedIndex}
                              onToggle={(isOpen, event) => {
                                if (event.source === 'click') {
                                  setSelectedIndex(isOpen ? index : undefined)
                                } else {
                                  setSelectedIndex(undefined)
                                }
                              }}
                            >
                              <Dropdown.Toggle className='btn btn-white table-three-dots dots-container'>
                                <img src={threeDots} alt='' className='three-dots-image' />
                              </Dropdown.Toggle>
                              <Dropdown.Menu className='m-2'>
                                <Dropdown.Item
                                  className='ps-5'
                                  onClick={() => {
                                    setIsCreate(false)
                                    setShareTransactionId(row.original.shareTransactionId)
                                    openModal('ShareTransactionDetails')
                                  }}
                                >
                                  <p className='mb-0'>{formatMessage({ id: 'View' })}</p>
                                </Dropdown.Item>
                                <Dropdown.Item
                                  className='text-dark ps-5'
                                  onClick={() => {
                                    setIsCreate(false)
                                    openModal('AddSharesTransaction')
                                    setShareTransactionId(row.original.shareTransactionId)
                                  }}
                                >
                                  {formatMessage({ id: 'Edit' })}
                                </Dropdown.Item>
                                <Dropdown.Item
                                  className='text-danger ps-5'
                                  onClick={() => {
                                    setShareTransactionId(row?.original?.shareTransactionId)
                                    setDeleteShowModal(true)
                                  }}
                                >
                                  <p className='mb-0'>{formatMessage({ id: 'Delete' })}</p>
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </Table>
          )}
        </div>

        {!loading && data?.length > 0 && pagination.totalCount > 10 && (
          <div className='w-100 d-flex justify-content-between align-items-center px-0 mt-8'>
            <div className='d-flex gap-2 w-100 align-items-center justify-content-start mb-4'>
              <select
                aria-label='Default select example'
                className={`p-4 py-3 w-80px border border-1 rounded-2 font-size-13 bg-white text-dark ${mode === 'dark' ? ' border-dark' : ''}`}
                value={pagination.pageSize}
                onChange={(e) => {
                  const newSize = Number(e.target.value)
                  setPagination((prev) => ({
                    ...prev,
                    pageSize: newSize,
                    pageIndex: 0, // Reset to first page when page size changes
                  }))
                }}
              >
                {[10, 20, 50, 100].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </select>

              <span className='font-size-13 text-center text-md-start'>
                {formatMessage({
                  id: `Showing ${pagination.currentPage === 1
                    ? 1
                    : (pagination.currentPage - 1) * pagination.pageSize + 1
                    } to ${pagination.nextPage ? data.length : pagination.totalCount} of ${pagination.totalCount
                    } records`,
                })}
              </span>
            </div>

            {/* <Pagination className='w-md-100 w-auto d-flex flex-end'>
              <Pagination.First onClick={() => handlePageChange(1)} disabled={!pagination.prevPage}>
                {'<<'}
              </Pagination.First>

              <Pagination.Prev
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.prevPage}
              >
                {'<'}
              </Pagination.Prev>

              {Array.from({ length: pagination.totalPages }, (_, i) => (
                <Pagination.Item
                  key={i}
                  active={i === pagination.currentPage - 1}
                  onClick={() => handlePageChange(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}

              <Pagination.Next
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.nextPage}
              >
                {'>'}
              </Pagination.Next>

              <Pagination.Last
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={!pagination.nextPage}
              >
                {'>>'}
              </Pagination.Last>
            </Pagination> */}
            <Pagination className='w-md-100 w-auto d-flex flex-end'>
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={pagination.currentPage === 1}
              >
                {'<<'}
              </Pagination.First>

              <Pagination.Prev
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
              >
                {'<'}
              </Pagination.Prev>

              {(() => {
                const items = [];
                const pageNeighbours = 2; // Number of neighbouring pages to show
                const startPage = Math.max(1, pagination.currentPage - pageNeighbours);
                const endPage = Math.min(pagination.totalPages, pagination.currentPage + pageNeighbours);

                // Always show the first page
                items.push(
                  <Pagination.Item
                    key={1}
                    active={pagination.currentPage === 1}
                    onClick={() => handlePageChange(1)}
                  >
                    1
                  </Pagination.Item>
                );

                // Add ellipsis if there are skipped pages before the current range
                if (startPage > 2) {
                  items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
                }

                // Add the current page range
                for (let i = startPage; i <= endPage; i++) {
                  items.push(
                    <Pagination.Item
                      key={i}
                      active={pagination.currentPage === i}
                      onClick={() => handlePageChange(i)}
                    >
                      {i}
                    </Pagination.Item>
                  );
                }

                // Add ellipsis if there are skipped pages after the current range
                if (endPage < pagination.totalPages - 1) {
                  items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
                }

                // Always show the last page
                if (pagination.totalPages > 1) {
                  items.push(
                    <Pagination.Item
                      key={pagination.totalPages}
                      active={pagination.currentPage === pagination.totalPages}
                      onClick={() => handlePageChange(pagination.totalPages)}
                    >
                      {pagination.totalPages}
                    </Pagination.Item>
                  );
                }

                return items;
              })()}

              <Pagination.Next
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
              >
                {'>'}
              </Pagination.Next>

              <Pagination.Last
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={pagination.currentPage === pagination.totalPages}
              >
                {'>>'}
              </Pagination.Last>
            </Pagination>


          </div>
        )}
      </div>
      {activeModal === 'AddSharesTransaction' && (
        <AddSharesModal
          isActive={true}
          setIsActive={closeModal}
          shareTransactionId={shareTransactionId}
          setIsShareTransactionCreateOrUpdate={setIsShareTransactionCreateOrUpdate}
          setSidebar={setSidebar}
          setModalType={setModalType}
          setIsCreate={setIsCreate}
          openModal={openModal}
          setIsShareClassDropdown={setIsShareClassDropdown}
        />
      )}
      {activeModal === 'ShareTransactionDetails' && shareTransactionId && (
        <ShareTransactionDetailsModal
          isActive={true}
          setIsActive={closeModal}
          shareTransactionId={shareTransactionId}
          setIsShareTransactionCreateOrUpdate={setIsShareTransactionCreateOrUpdate}
          isShareTransactionCreateOrUpdate={isShareTransactionCreateOrUpdate}
          setSidebar={setSidebar}
          setIsShareClassDropdown={setIsShareClassDropdown}
          setModalType={setModalType}
          setIsCreate={setIsCreate}
        />
      )}
      <DeleteFounderPromoterModal
        setDeleteShowModal={setDeleteShowModal}
        showDeleteModal={showDeleteModal}
        onDeleteSubmit={handleDeleteShareTransaction}
        deleteLoading={deleteLoading}
        header='Delete Share Transaction'
        paragraph='Are you sure you want to delete this share transaction?'
      />
    </>
  )
}
